import { useMutation } from '@apollo/client'
import { Edit } from '@mui/icons-material'
import {
    Button,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { MutableRefObject, useEffect, useState } from 'react'
import { GetRosterSearchV2 } from 'recruiter/contexts'
import { UpdateRosterCandidateRecruiterNote } from 'recruiter/graphql/rosters.gql'
import { getClientNoteAuthor } from 'shared/components/clientNote'
import useToast from 'shared/components/Toast/useToast'
import { filterLabelSx } from 'recruiter/filterStyles'
import { getPxAsRem } from 'shared/utils'

export interface RosterCandidateRecruiterNoteProps {
    noteData: RecruiterNoteData
    isLoading: boolean
    onSave: () => void
    label?: string
    rosterCandidateId: string
    isInternalNote: boolean
    isEditing?: boolean
    onStartEdit?: () => void
    onCancelEdit?: () => void
    editingDisabled?: boolean
    saveFnRef?: MutableRefObject<(() => void) | null>
}

export interface RecruiterNoteData {
    note?: string
    updatedAt?: Date
    createdAt?: Date
    updatedByUser?: {
        preferredName?: string
    }
    createdByUser?: {
        preferredName?: string
    }
    canEdit: boolean
}

export function getAuditLabel(
    updatedAt?: Date,
    createdAt?: Date,
    updatedByName?: string,
    createdByName?: string
): string {
    if ((!updatedAt && !createdAt) || (!updatedByName && !createdByName))
        return ''
    const name =
        updatedByName && updatedByName !== '' ? updatedByName : createdByName
    return updatedByName !== '' || createdByName !== ''
        ? getClientNoteAuthor(createdAt ?? new Date(), name ?? '', updatedAt)
        : ''
}

const RosterCandidateRecruiterNote: React.FC<
    RosterCandidateRecruiterNoteProps
> = ({
    rosterCandidateId,
    noteData,
    onSave,
    isInternalNote,
    isLoading = false,
    isEditing: isEditingProp,
    onStartEdit: onStartEditProp,
    onCancelEdit: onCancelEditProp,
    editingDisabled,
    saveFnRef,
}) => {
    const toast = useToast()
    const [updateNote] = useMutation(UpdateRosterCandidateRecruiterNote)
    const [noteContent, setNoteContent] = useState(noteData.note)
    const [auditLabel, setAuditLabel] = useState<string>(
        getAuditLabel(
            noteData.updatedAt,
            noteData.createdAt,
            noteData.updatedByUser?.preferredName,
            noteData.createdByUser?.preferredName
        )
    )

    const [internalIsEditing, setInternalIsEditing] = useState(false)
    const isEditing = isEditingProp ?? internalIsEditing
    const handleStartEdit =
        onStartEditProp ?? (() => setInternalIsEditing(true))
    const handleCancelEdit =
        onCancelEditProp ?? (() => setInternalIsEditing(false))
    const showInlineButtons = !saveFnRef

    useEffect(() => {
        setNoteContent(noteData.note)
        setAuditLabel(
            getAuditLabel(
                noteData.updatedAt,
                noteData.createdAt,
                noteData.updatedByUser?.preferredName,
                noteData.createdByUser?.preferredName
            )
        )
    }, [noteData])

    useEffect(() => {
        if (!isEditing) {
            setNoteContent(noteData.note)
        }
    }, [isEditing])

    const doSave = () => {
        updateNote({
            variables: {
                rosterCandidateId: rosterCandidateId,
                note: noteContent,
                isInternalOnly: isInternalNote,
            },
            refetchQueries: [GetRosterSearchV2, 'GetRosterCandidatePanelData'],
            onCompleted: (res) => {
                const success = res.updateRosterCandidateRecruiterNote?.success
                toast({
                    title: success ? 'Success!' : 'Error',
                    message:
                        res.updateRosterCandidateRecruiterNote?.message ?? '',
                    severity: success ? 'success' : 'error',
                })
                if (success) {
                    handleCancelEdit()
                    onSave()
                }
            },
            onError: (error) => {
                console.error('Error updating note:', error)
                toast({
                    title: 'Error',
                    message: error.message,
                    severity: 'error',
                })
            },
        })
    }

    if (isEditing && saveFnRef) {
        saveFnRef.current = doSave
    }

    return (
        <Stack direction={'column'} spacing={1}>
            <Stack
                direction="row"
                alignItems={'center'}
                justifyContent={'space-between'}
            >
                <Typography sx={filterLabelSx}>
                    {isInternalNote ? 'Internal' : 'Client Visible'}
                </Typography>
                <Button
                    variant="text"
                    size="small"
                    disabled={!noteData.canEdit || isEditing || editingDisabled}
                    onClick={handleStartEdit}
                    sx={{ ...filterLabelSx, p: 0 }}
                >
                    <Edit sx={{ fontSize: '0.75rem', mr: 0.5, py: 0 }} />
                    <Typography sx={filterLabelSx}>Edit</Typography>
                </Button>
            </Stack>
            <TextField
                variant="outlined"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Add feedback here..."
                multiline
                fullWidth
                rows={4}
                disabled={isLoading || !isEditing}
                sx={{
                    minHeight: getPxAsRem(100),
                    '& .MuiOutlinedInput-root': {
                        fontSize: '0.75rem',
                        '& fieldset': { borderColor: 'divider' },
                        '&:hover fieldset': { borderColor: 'text.secondary' },
                        '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.75)',
                        },
                    },
                }}
            >
                {isLoading && <CircularProgress />}
            </TextField>

            {showInlineButtons && isEditing && (
                <Stack
                    direction="row"
                    justifyContent={'flex-end'}
                    alignItems={'center'}
                    alignContent={'center'}
                    justifyItems={'space-evenly'}
                    spacing={2}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            handleCancelEdit()
                        }}
                        sx={{
                            width: getPxAsRem(90),
                            fontSize: '0.75rem',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={doSave}
                        sx={{
                            width: getPxAsRem(90),
                            fontSize: '0.75rem',
                            textTransform: 'none',
                        }}
                    >
                        Save
                    </Button>
                </Stack>
            )}
            <Typography
                sx={{
                    fontSize: '0.625rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                }}
                variant="detail"
            >
                {auditLabel}
            </Typography>
        </Stack>
    )
}
export default RosterCandidateRecruiterNote
